import { useEffect } from 'react';

type Wheel = { deltaX: number; deltaY: number; ctrlKey: boolean };
type Target = { editable: boolean; canScrollX: (dir: -1 | 1) => boolean; overBar: boolean };

/**
 * Backlog §19: a two-finger swipe on a Mac trackpad must not send the browser back from the editor. A wheel mostly
 * horizontal is cancelled unless a text field or a horizontal scroller that can still move that way is under it;
 * vertical wheels (panel scrolling) are never touched. ctrl+wheel (trackpad pinch) over a bar would zoom the page.
 */
export function shouldBlockWheel(e: Wheel, t: Target): boolean {
  if (e.ctrlKey) return t.overBar;
  if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return false;
  if (t.editable) return false;
  return !t.canScrollX(e.deltaX > 0 ? 1 : -1);
}

const EDITABLE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';
const BAR = '[data-topbar], [role="toolbar"]';

/** What is under a wheel: a text field, a bar, and whether an ancestor can still scroll horizontally in a direction. */
export function wheelTarget(el: Element | null): Target {
  return {
    editable: !!el?.closest(EDITABLE),
    overBar: !!el?.closest(BAR),
    canScrollX: (dir) => {
      for (let n: Element | null = el; n; n = n.parentElement) {
        const h = n as HTMLElement;
        if (h.scrollWidth <= h.clientWidth) continue;
        const ox = getComputedStyle(h).overflowX;
        if (ox !== 'auto' && ox !== 'scroll') continue;
        if (dir > 0 ? h.scrollLeft + h.clientWidth < h.scrollWidth - 1 : h.scrollLeft > 0) return true;
      }
      return false;
    },
  };
}

/**
 * While the editor is mounted: `overscroll-behavior: none` on the page (Chromium's swipe navigation) and a page-wide
 * non-passive wheel listener (bars and panels live outside the canvas, some in portals). The canvas handles its own
 * wheel; this only cancels the browser's default.
 */
export function useEditorGestureGuard(): void {
  useEffect(() => {
    const html = document.documentElement.style;
    const body = document.body.style;
    const before = [html.overscrollBehavior, body.overscrollBehavior] as const;
    html.overscrollBehavior = 'none';
    body.overscrollBehavior = 'none';
    const onWheel = (e: WheelEvent) => {
      if (shouldBlockWheel(e, wheelTarget(e.target instanceof Element ? e.target : null))) e.preventDefault();
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('wheel', onWheel);
      html.overscrollBehavior = before[0];
      body.overscrollBehavior = before[1];
    };
  }, []);
}
